import { createAction, handleActions } from 'redux-actions';
import { all, call, put, takeLatest } from 'redux-saga/effects';
import { AxiosError } from 'axios';
import { CustomAxios, getErrorMessage } from '../../../axios';
import { LocationSearchModel } from '../../../../models/location-search.model';
import { OrgSearchModel } from '../../../../models/org-search.model';
import { showError } from '@weave/alert-system';
import { Store } from '../../../store/store.model';
import { searchOrgs } from '../../../../apis/identity-search/api';

export const locationSearch = createAction<string>('LOCATION_SEARCH');
export const locationSearchSuccess = createAction<LocationSearchModel[]>(
  'LOCATION_SEARCH_SUCCESS'
);
export const locationSearchFailure = createAction('LOCATION_SEARCH_FAILURE');

export const userLocationsSearch = createAction<string>('USER_LOCATIONS_SEARCH');
export const userLocationsSearchSuccess = createAction('USER_LOCATIONS_SEARCH_SUCCESS');
export const userLocationsSearchFailure = createAction('USER_LOCATIONS_SEARCH_FAILURE');

export const locationChildrenSearch = createAction<string>('LOCATION_CHILDREN_SEARCH');
export const locationChildrenSearchSuccess = createAction(
  'LOCATION_CHILDREN_SEARCH_SUCCESS'
);
export const locationChildrenSearchFailure = createAction(
  'LOCATION_CHILDREN_SEARCH_FAILURE'
);
export const clearLocationChildren = createAction('CLEAR_LOCATION_CHILDREN');

// orgSearch is backed by the IAM identity SearchOrgs gRPC endpoint (see
// iam/internal/grpc/org.go), rather than the legacy /support/v1 REST endpoints used
// by handleLocationSearch above. Selecting an org navigates to a dedicated org
// details page (see org-details-page.component.tsx) which uses GetOrgHierarchy.
export const orgSearch = createAction<string>('ORG_SEARCH');
export const orgSearchSuccess = createAction<OrgSearchModel[]>('ORG_SEARCH_SUCCESS');
export const orgSearchFailure = createAction('ORG_SEARCH_FAILURE');

export const handleLocationSearch = function* (action) {
  try {
    const query = action.payload;
    const response = yield call(CustomAxios.get, `/support/v1/locations/search/${query}`);
    yield put(locationSearchSuccess(response.data.data));
  } catch (error: any) {
    yield put(locationSearchFailure());
    yield put(showError(getErrorMessage(error)));
  }
};

export const handleUserLocationsSearch = function* (action) {
  try {
    const userId = action.payload;
    const response = yield call(CustomAxios.get, `/support/v1/users/locations/${userId}`);

    const locations = response.data.data.Locations;
    const username = response.data.data.Username;

    const responses = yield all(
      locations.map((location) =>
        call(CustomAxios.get, `/support/v1/locations/${location.ID}`)
      )
    );

    const userLocations = responses.reduce((acc, resp) => {
      return [...acc, { ...resp.data.data, Username: username }];
    }, []);
    yield put(userLocationsSearchSuccess(userLocations));
  } catch (error: any) {
    yield put(userLocationsSearchFailure());
    yield put(showError(getErrorMessage(error)));
  }
};

export const handleLocationChildrenSearch = function* (action) {
  try {
    const locationId = action.payload;
    const response = yield call(
      CustomAxios.get,
      `/support/v1/locations/${locationId}/children`
    );
    yield put(locationChildrenSearchSuccess(response.data.data));
  } catch (error: any) {
    yield put(locationChildrenSearchFailure());
    yield put(showError(getErrorMessage(error)));
  }
};

export const handleOrgSearch = function* (action) {
  try {
    const query = action.payload;
    const orgs = yield call(searchOrgs, query);
    const orgSearchResults: OrgSearchModel[] = orgs.map((result) => ({
      OrgId: result.org?.orgId ?? '',
      Name: result.org?.name ?? '',
    }));
    yield put(orgSearchSuccess(orgSearchResults));
  } catch (error) {
    yield put(orgSearchFailure());
    yield put(showError(getErrorMessage(error as AxiosError)));
  }
};

export const locationSearchSaga = function* () {
  yield all([
    takeLatest(locationSearch.toString(), handleLocationSearch),
    takeLatest(userLocationsSearch.toString(), handleUserLocationsSearch),
    takeLatest(locationChildrenSearch.toString(), handleLocationChildrenSearch),
    takeLatest(orgSearch.toString(), handleOrgSearch),
  ]);
};

const defaultState = {
  loading: false,
  isLocationLoading: false,
  isOrgLoading: false,
  locations: [],
  userLocations: [],
  locationChildren: [],
  orgs: [],
};

export type LocationSearchState = {
  loading: boolean;
  isLocationLoading: boolean;
  isOrgLoading: boolean;
  locations?: LocationSearchModel[];
  userLocations: any[]; // todo: figure out how this should be shaped
  locationChildren: {
    LocationID: string;
    Name: string;
    Slug: string;
    PhoneTenantID: string;
  }[]; //these is the only property being used from what I can tell. Maybe it's a LocationModel?
  orgs: OrgSearchModel[];
};

export const locationSearchReducer = handleActions(
  {
    [locationSearch.toString()]: (state) =>
      Object.assign({}, state, { isLocationLoading: true }),
    [locationSearchSuccess.toString()]: (state, action) =>
      Object.assign({}, state, {
        locations: action.payload,
        isLocationLoading: false,
      }),
    [locationSearchFailure.toString()]: (state) =>
      Object.assign({}, state, { isLocationLoading: false }),

    [userLocationsSearch.toString()]: (state) =>
      Object.assign({}, state, { loading: true }),
    [userLocationsSearchSuccess.toString()]: (state, action) =>
      Object.assign({}, state, {
        userLocations: action.payload,
        loading: false,
      }),
    [userLocationsSearchFailure.toString()]: (state) =>
      Object.assign({}, state, { loading: false }),

    [locationChildrenSearch.toString()]: (state) =>
      Object.assign({}, state, { loading: true }),
    [locationChildrenSearchSuccess.toString()]: (state, action) =>
      Object.assign({}, state, {
        locationChildren: action.payload,
        loading: false,
      }),
    [locationChildrenSearchFailure.toString()]: (state) =>
      Object.assign({}, state, { loading: false }),
    [clearLocationChildren.toString()]: (state) =>
      Object.assign({}, state, { loading: false, locationChildren: [] }),

    [orgSearch.toString()]: (state) => Object.assign({}, state, { isOrgLoading: true }),
    [orgSearchSuccess.toString()]: (state, action) =>
      Object.assign({}, state, {
        orgs: action.payload,
        isOrgLoading: false,
      }),
    [orgSearchFailure.toString()]: (state) =>
      Object.assign({}, state, { isOrgLoading: false }),
  },
  defaultState
);

export const selectLocationChildren = (state: Store) => {
  return state.locationSearch?.locationChildren ?? [];
};
